import React, { useContext, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
    CitiesContainer,
    Container,
    SearchContainer,
    SpinnerContainer,
    Subtitle,
    Title,
    ToggleIcon,
    ToggleText
} from './LocationSearch.style'
import PlacesAutocomplete from '@components/PlacesAutocomplete/PlacesAutocomplete.component'
import { MapLocation } from '@customTypes/mapLocation.interface'
import ListView from './components/ListView/ListView.component'
import MapView from './components/MapView/MapView.component'
import { CITIES, City as ICity } from './cities.const'
import City from './components/City/City.component'
import { useQuery } from '@apollo/client'
import { B2C_GET_CARPAY_LOTS } from '@graphql/queries/getCarpayLots.query'
import { B2cGetCarpayLotsQuery, B2cGetCarpayLotsQueryVariables } from 'parkdepot-shared/gql/graphql'
import { CarpayLot } from '@customTypes/carpayLot.interface'
import Spinner from 'parkdepot-shared/components/Spinner'
import { useHistory, useLocation } from 'react-router-dom'
import { ErrorFlowContext } from '@context/ErrorFlowContext/errorFlow.context'
import { carpayLPNotFoundStayRoute } from '../route.const'
import LotViewToggle from '@components/LotViewToggle/LotViewToggle.component'

enum VIEW {
    DEFAULT,
    LIST,
    MAP
}

// roughly about 20km radius
const xTolerance = 0.18
const yTolerance = 0.27

const CarpayLocationSearch = () => {
    const { t } = useTranslation()
    const location = useLocation()
    const history = useHistory()
    const { setErrorFlow } = useContext(ErrorFlowContext)

    const [carpayLots, setCarpayLots] = useState<CarpayLot[]>([])
    const [view, setView] = useState<VIEW>(VIEW.DEFAULT)
    const [searchedLocation, setSearchedLocation] = useState<MapLocation | null>(null)
    const [lotMapSelected, setLotMapSelected] = useState<string>()
    const [filteredLots, setFilteredLots] = useState<CarpayLot[]>([])
    const [filteredCities, setFilteredCities] = useState<ICity[]>([...CITIES])

    const { data: lots, loading } = useQuery<B2cGetCarpayLotsQuery, B2cGetCarpayLotsQueryVariables>(B2C_GET_CARPAY_LOTS)

    const onLocationSelect = (place: any) => {
        setSearchedLocation({
            lat: place.lat,
            lng: place.lng,
            address: place.address
        } as MapLocation)

        if (view === VIEW.DEFAULT) {
            setView(VIEW.LIST)
        }
    }

    const onCityClick = (city: ICity) => {
        setSearchedLocation({ lat: city.latitude, lng: city.longitude, address: t(city.name) } as MapLocation)
        setView(VIEW.LIST)
    }

    const onLotClick = (lot: CarpayLot) => {
        const queryParams = new URLSearchParams(location.search)
        // get plate and country from url params
        const plate = queryParams.get('plate')
        const country = queryParams.get('country')
        setErrorFlow({
            lot: {
                id: lot.lot_id,
                name: lot.name ?? ''
            },
            plate,
            country
        })

        // redirect to stay page, add lotid to query params and keep the rest
        history.push(`${carpayLPNotFoundStayRoute}${location.search}&lotId=${lot.lot_id}`)
    }

    const onInputChange = (value: string) => {
        if (value === '') {
            setFilteredCities([...CITIES])
            setView(VIEW.DEFAULT)
            setSearchedLocation(null)
            return
        }
        if (filteredCities.length === 0) {
            return
        }
        // filter cities based on input
        const filtered = filteredCities.filter((city) => city.name.toLowerCase().includes(value.toLowerCase()))
        setFilteredCities(filtered)
    }

    useEffect(() => {
        setCarpayLots(lots?.b2c_get_carpay_lots.lots as CarpayLot[])
        setFilteredLots((lots?.b2c_get_carpay_lots.lots as CarpayLot[]) || [])
    }, [lots])

    useEffect(() => {
        if (searchedLocation && carpayLots && carpayLots.length > 0) {
            const matchingLots = carpayLots.filter(
                (lot) =>
                    Math.abs(lot.latitude - searchedLocation.lat) < yTolerance &&
                    Math.abs(lot.longitude - searchedLocation.lng) < xTolerance
            )
            setFilteredLots(matchingLots)
        }
    }, [searchedLocation])

    if (loading) {
        return (
            <Container>
                <SpinnerContainer>
                    <Spinner theme="primary" size="xl" />
                </SpinnerContainer>
            </Container>
        )
    }

    return (
        <Container>
            {view === VIEW.DEFAULT && <Title>{t('carpay.notFound.search.title')}</Title>}
            <SearchContainer>
                <Subtitle $big={view !== VIEW.DEFAULT}>{t('carpay.notFound.search.searchByCity')}</Subtitle>
                <PlacesAutocomplete
                    onPlaceSelected={onLocationSelect}
                    preselectedLocation={searchedLocation}
                    onInputChange={onInputChange}
                />
            </SearchContainer>
            {view === VIEW.LIST && <ListView lots={filteredLots} onLotClick={onLotClick} />}
            {view === VIEW.MAP && (
                <MapView
                    publicLots={carpayLots}
                    searchedLocation={searchedLocation}
                    setLotMapSelected={setLotMapSelected}
                    onLotClick={onLotClick}
                />
            )}

            {view === VIEW.DEFAULT && (
                <CitiesContainer>
                    {filteredCities.map((city) => (
                        <City key={city.id} city={city} onClick={onCityClick} />
                    ))}
                </CitiesContainer>
            )}

            {/* list view or maps view */}
            <LotViewToggle
                cardShown={view === VIEW.MAP && !!lotMapSelected}
                onClick={() => {
                    if (view === VIEW.DEFAULT || view === VIEW.LIST) {
                        setView(VIEW.MAP)
                    } else {
                        setView(VIEW.LIST)
                    }
                }}
            >
                <ToggleIcon
                    className={`${view === VIEW.LIST || view === VIEW.DEFAULT ? 'wemolo-map-search' : 'pkd-list'}`}
                    aria-hidden="true"
                />
                <ToggleText>
                    {view === VIEW.LIST || view === VIEW.DEFAULT ? t('search.map') : t('search.list')}
                </ToggleText>
            </LotViewToggle>
        </Container>
    )
}

export default CarpayLocationSearch
